import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCallback, useRef, useState } from 'react';
import { OnDatePickerChange } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { DesignLayoutType, EventType } from '@graphql/generated';

import { useSetTheme } from '@shared/createEvent/hooks/useSetTheme';
import { useFieldErrors } from '@shared/createEvent/hooks/useFieldErrors';
import { useDesignParams } from '@shared/createEvent/hooks/useDesignParams';
import { useAllowedDesignParams } from '@shared/createEvent/AllowedDesignParamsProvider';
import { CreateEventValuesProps, useCreateEvent } from '@shared/createEvent/hooks/useCreateEvent';
import { SurveyItems, useEventProductVerticals } from '@shared/createEvent/hooks/useEventProductVerticals';

import { useCreateBabyRegistryTelemetry } from '@apps/createBabyRegistry/CreateBabyRegistry.telemetry';
import { useViewContext } from '@apps/createBabyRegistry/components/View';

interface CreateBabyEventValuesProps extends CreateEventValuesProps {
  blockEventDate: boolean;
}

export const useCreateJoyEventWrapperController = () => {
  const { handleCreateEvent } = useCreateEvent();
  const { t } = useTranslation('createBabyRegistry');
  const { designParams } = useDesignParams();
  const telemetry = useCreateBabyRegistryTelemetry();
  const { themes } = useAllowedDesignParams();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [blockEventDate, setBlockEventDate] = useState<boolean>(false);
  const updateEventProductVerticals = useEventProductVerticals();
  const updateTheme = useSetTheme();
  const { changeView } = useViewContext();

  const eventFormTranslations = t('createEventForm');
  const errorTranslations = t('createEventForm', 'errors');

  const handleSubmit = useCallback(
    (values: CreateEventValuesProps) => {
      const eventDate = blockEventDate ? null : values.eventDate;
      const eventDisplayName = `${values.ownerFirstName}${values.ownerFirstName?.endsWith('s') ? "'" : "'s"} Baby Registry`;
      handleCreateEvent({
        values: {
          ...values,
          eventDate,
          eventDisplayName
        },
        telemetry
      });
      updateTheme({ themeId: themes.get('blank') ?? '' });
      updateEventProductVerticals({ selectedOptions: { [SurveyItems.REGISTRY]: true } });
      changeView('tyingTheKnot');
    },
    [blockEventDate, themes, telemetry, handleCreateEvent, updateEventProductVerticals, updateTheme, changeView]
  );

  const formik = useFormik<CreateBabyEventValuesProps>({
    initialValues: {
      eventType: EventType.babyRegistry,
      ownerFirstName: '',
      ownerLastName: '',
      eventDate: null,
      blockEventDate,
      ...designParams
    },
    validationSchema: Yup.object<CreateBabyEventValuesProps>({
      eventType: Yup.mixed<EventType>().oneOf(Object.values(EventType)).required(),
      ownerFirstName: Yup.string().required(errorTranslations.ownerFirstNameRequired()),
      ownerLastName: Yup.string().required(errorTranslations.ownerLastNameRequired()),
      eventDate: Yup.date().when('blockEventDate', {
        is: false,
        then: Yup.date().nullable().required(errorTranslations.registryDateRequired()),
        otherwise: Yup.date().nullable()
      }),
      blockEventDate: Yup.boolean(),
      font: Yup.string(),
      template: Yup.mixed<DesignLayoutType>().oneOf(Object.values(DesignLayoutType)),
      scenario: Yup.string(),
      theme: Yup.string()
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: handleSubmit
  });

  const handleDateChange: OnDatePickerChange = useCallback(
    date => {
      formik.setFieldValue('eventDate', date).finally(() => {
        telemetry.dateSelected();
        submitButtonRef.current?.focus();
        setBlockEventDate(false);
        formik.setFieldValue('blockEventDate', false);
        formik.validateField('eventDate');
      });
    },
    [formik, telemetry]
  );

  const handleBlockDate = useCallback(
    e => {
      setBlockEventDate(e.target.checked);
      telemetry.dateUndecidedClicked();
      formik.setFieldValue('eventDate', null);
      formik.setFieldValue('blockEventDate', e.target.checked);
    },
    [formik, telemetry]
  );

  const getFieldErrorProps = useFieldErrors<CreateEventValuesProps>(formik);

  return { t, submitButtonRef, formik, blockEventDate, telemetry, eventFormTranslations, getFieldErrorProps, handleDateChange, handleBlockDate };
};
